import { routePaths } from 'src/utils/constants';

/**
 * NPT-1971: keeps the WorkOS detour out of the back stack. Leaving for WorkOS records where the
 * traveler was; the WorkOS callback walks history back to that entry instead of pushing the
 * return URL on top of the hosted login pages, so Back from the returned page goes to the step
 * before it. WorkOS's own entries can't be removed — they are left as forward entries.
 */

const AUTH_ORIGIN_KEY = 'npt_auth_origin';
const AUTH_RETURN_KEY = 'npt_auth_return';
// Chrome and Firefox stop growing history.length at 50, after which it no longer locates the origin entry.
const HISTORY_LENGTH_CAP = 50;
// A traversal that never reached our origin entry must not hijack a later visit.
const AUTH_RETURN_TTL_MS = 30_000;
const REDIRECT_ONLY_PATHS = [routePaths.LOGIN_PAGE, routePaths.REGISTER_PAGE, routePaths.SIGNUP];

type AuthOrigin = { href: string; historyLength: number };
type AuthReturn = { originHref: string; destination: string; savedAt: number };

const take = <T>(key: string): T | null => {
   try {
      const raw = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
   } catch {
      return null;
   }
};

const save = (key: string, value: AuthOrigin | AuthReturn) => {
   try {
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
   } catch {
      return false;
   }
};

export const hasPendingAuthReturn = () => {
   try {
      return !!sessionStorage.getItem(AUTH_RETURN_KEY);
   } catch {
      return false;
   }
};

/**
 * The pushed duplicate is what location.replace overwrites, so the current page keeps its own
 * entry. pushState also drops any forward entries, which makes history.length pin its index.
 */
export const leaveForAuthProvider = (authUrl: string) => {
   try {
      window.history.pushState(window.history.state, '', window.location.href);
      save(AUTH_ORIGIN_KEY, { href: window.location.href, historyLength: window.history.length });
   } catch {
      /* history or storage blocked: fall back to a plain redirect */
   }
   window.location.replace(authUrl);
};

export const returnFromAuthProvider = (returnTo?: string) => {
   const origin = take<AuthOrigin>(AUTH_ORIGIN_KEY);
   const steps = origin ? window.history.length - origin.historyLength + 1 : 0;

   if (origin && steps >= 1 && window.history.length < HISTORY_LENGTH_CAP) {
      const destination = new URL(returnTo || routePaths.LANDING, window.location.origin).href;
      if (save(AUTH_RETURN_KEY, { originHref: origin.href, destination, savedAt: Date.now() })) {
         // authkit rewrites this entry's URL right after the callback returns; traverse after it.
         setTimeout(() => window.history.go(-steps));
         return;
      }
   }

   if (returnTo) window.location.replace(returnTo);
};

/** Runs on every non-callback page load and back-forward cache restore. Returns true when it navigates away. */
export const settleAuthReturn = (restoredFromCache: boolean) => {
   // The callback page still has to read the origin marker; settling here would consume it.
   if (new URL(window.location.href).searchParams.has('code')) return false;

   take(AUTH_ORIGIN_KEY);
   const authReturn = take<AuthReturn>(AUTH_RETURN_KEY);
   if (!authReturn || Date.now() - authReturn.savedAt > AUTH_RETURN_TTL_MS) return false;

   const here = window.location.href;
   if (here !== authReturn.originHref) {
      window.location.replace(authReturn.destination);
      return true;
   }

   if (here === authReturn.destination) {
      // A cached page still renders the signed-out state it was left in.
      if (restoredFromCache) window.location.reload();
      return restoredFromCache;
   }

   const { pathname } = new URL(here);
   if (REDIRECT_ONLY_PATHS.some(path => pathname.includes(path))) {
      window.location.replace(authReturn.destination);
   } else {
      window.location.assign(authReturn.destination);
   }
   return true;
};
